import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PricingCalculator = () => {
  const [selectedService, setSelectedService] = useState('web-app');
  const [projectComplexity, setProjectComplexity] = useState('medium');
  const [timeline, setTimeline] = useState('standard');
  const [additionalServices, setAdditionalServices] = useState([]);
  const [estimatedCost, setEstimatedCost] = useState(0);

  const serviceOptions = [
    { value: 'web-app', label: 'Web Application', basePrice: 15000 },
    { value: 'mobile-app', label: 'Mobile Application', basePrice: 25000 },
    { value: 'custom-software', label: 'Custom Software', basePrice: 35000 },
    { value: 'ui-ux-design', label: 'UI/UX Design', basePrice: 8000 }
  ];

  const complexityOptions = [
    { value: 'simple', label: 'Simple', multiplier: 0.7 },
    { value: 'medium', label: 'Medium', multiplier: 1.0 },
    { value: 'complex', label: 'Complex', multiplier: 1.5 },
    { value: 'enterprise', label: 'Enterprise', multiplier: 2.0 }
  ];

  const timelineOptions = [
    { value: 'rush', label: 'Rush (2-4 weeks)', multiplier: 1.5 },
    { value: 'standard', label: 'Standard (6-12 weeks)', multiplier: 1.0 },
    { value: 'extended', label: 'Extended (3-6 months)', multiplier: 0.9 }
  ];

  const additionalServiceOptions = [
    { id: 'seo', label: 'SEO Optimization', price: 2500 },
    { id: 'analytics', label: 'Analytics Setup', price: 1500 },
    { id: 'maintenance', label: '6-Month Maintenance', price: 5000 },
    { id: 'training', label: 'Team Training', price: 3000 },
    { id: 'api-integration', label: 'Third-party API Integration', price: 4000 },
    { id: 'security-audit', label: 'Security Audit', price: 3500 }
  ];

  const pricingTiers = [
    {
      name: "Starter",
      description: "Perfect for small businesses and startups",
      features: [
        "Basic web application",
        "Responsive design",
        "Contact form integration",
        "Basic SEO setup",
        "3 months support"
      ],
      startingPrice: 5000,
      color: "accent",
      icon: "Zap"
    },
    {
      name: "Professional",
      description: "Ideal for growing businesses",
      features: [
        "Advanced web application",
        "Custom UI/UX design",
        "Database integration",
        "Payment gateway",
        "6 months support",
        "Performance optimization"
      ],
      startingPrice: 15000,
      color: "success",
      icon: "Briefcase",
      popular: true
    },
    {
      name: "Enterprise",
      description: "For large-scale applications",
      features: [
        "Complex web/mobile application",
        "Advanced integrations",
        "Custom admin panel",
        "Multi-user roles",
        "12 months support",
        "Dedicated project manager",
        "Security audit included"
      ],
      startingPrice: 35000,
      color: "warning",
      icon: "Building"
    }
  ];

  useEffect(() => {
    calculateEstimate();
  }, [selectedService, projectComplexity, timeline, additionalServices]);

  const calculateEstimate = () => {
    const service = serviceOptions?.find(s => s?.value === selectedService);
    const complexity = complexityOptions?.find(c => c?.value === projectComplexity);
    const timelineOption = timelineOptions?.find(t => t?.value === timeline);
    
    let basePrice = service?.basePrice || 0;
    let complexityMultiplier = complexity?.multiplier || 1;
    let timelineMultiplier = timelineOption?.multiplier || 1;
    
    let additionalCost = additionalServices?.reduce((total, serviceId) => {
      const service = additionalServiceOptions?.find(s => s?.id === serviceId);
      return total + (service?.price || 0);
    }, 0);

    const totalCost = (basePrice * complexityMultiplier * timelineMultiplier) + additionalCost;
    setEstimatedCost(Math.round(totalCost));
  };

  const handleAdditionalServiceChange = (serviceId, checked) => {
    if (checked) {
      setAdditionalServices([...additionalServices, serviceId]);
    } else {
      setAdditionalServices(additionalServices?.filter(id => id !== serviceId));
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Calculator" size={16} />
            <span>Pricing Calculator</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Get Instant
            <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
              Project Estimate
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Use our interactive pricing calculator to get an instant estimate for your project. 
            Customize your requirements and see real-time pricing updates.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calculator Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="bg-card border border-border rounded-2xl p-8 shadow-3d">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center space-x-3">
                <Icon name="Settings" size={24} className="text-accent" />
                <span>Project Configuration</span>
              </h3>

              <div className="space-y-6">
                {/* Service Type */}
                <div>
                  <Select
                    label="Service Type"
                    description="Choose the primary service you need"
                    options={serviceOptions}
                    value={selectedService}
                    onChange={setSelectedService}
                    className="mb-4"
                  />
                </div>

                {/* Project Complexity */}
                <div>
                  <Select
                    label="Project Complexity"
                    description="Select the complexity level of your project"
                    options={complexityOptions}
                    value={projectComplexity}
                    onChange={setProjectComplexity}
                    className="mb-4"
                  />
                </div>

                {/* Timeline */}
                <div>
                  <Select
                    label="Project Timeline"
                    description="Choose your preferred timeline"
                    options={timelineOptions}
                    value={timeline}
                    onChange={setTimeline}
                    className="mb-4"
                  />
                </div>

                {/* Additional Services */}
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-4">Additional Services</h4>
                  <div className="space-y-3">
                    {additionalServiceOptions?.map((service) => (
                      <Checkbox
                        key={service?.id}
                        label={`${service?.label} (+₹${service?.price?.toLocaleString()})`}
                        checked={additionalServices?.includes(service?.id)}
                        onChange={(e) => handleAdditionalServiceChange(service?.id, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Estimate Result */}
            <motion.div
              key={estimatedCost}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-br from-accent/10 to-success/10 border border-accent/20 rounded-2xl p-8 text-center"
            >
              <h3 className="text-2xl font-bold text-foreground mb-4">Estimated Project Cost</h3>
              <div className="text-5xl font-bold text-accent mb-4">
                ₹{estimatedCost?.toLocaleString()}
              </div>
              <p className="text-muted-foreground mb-6">
                This is an estimated cost based on your selections. Final pricing may vary based on specific requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="default"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent"
                >
                  Get Detailed Quote
                </Button>
                <Button
                  variant="outline"
                  iconName="Calendar"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  Schedule Consultation
                </Button>
              </div>
            </motion.div>
          </motion.div>

          {/* Pricing Tiers */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">Service Packages</h3>
              <p className="text-muted-foreground">
                Choose from our pre-designed packages or customize your own solution
              </p>
            </div>

            {pricingTiers?.map((tier, index) => (
              <motion.div
                key={tier?.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-card border rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 ${
                  tier?.popular 
                    ? `border-${tier?.color}/30 shadow-${tier?.color}/20` 
                    : 'border-border'
                }`}
              >
                {tier?.popular && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 bg-${tier?.color} text-white px-4 py-1 rounded-full text-sm font-medium`}>
                    Most Popular
                  </div>
                )}

                <div className="flex items-center space-x-4 mb-4">
                  <div className={`w-12 h-12 bg-${tier?.color}/20 rounded-xl flex items-center justify-center`}>
                    <Icon name={tier?.icon} size={24} className={`text-${tier?.color}`} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground">{tier?.name}</h4>
                    <p className="text-sm text-muted-foreground">{tier?.description}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-3xl font-bold text-foreground">
                    ₹{tier?.startingPrice?.toLocaleString()}
                    <span className="text-sm font-normal text-muted-foreground">+ starting</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {tier?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <Icon name="Check" size={16} className={`text-${tier?.color}`} />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={tier?.popular ? "default" : "outline"}
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                  className={tier?.popular 
                    ? `bg-${tier?.color} text-white hover:bg-${tier?.color}/90`
                    : `border-${tier?.color}/30 text-${tier?.color} hover:bg-${tier?.color}/10`
                  }
                >
                  Choose {tier?.name}
                </Button>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Pricing Notes */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-muted/30 border border-border rounded-xl p-6 max-w-4xl mx-auto">
            <h4 className="font-semibold text-foreground mb-4">Pricing Information</h4>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-accent" />
                <span>Fixed-price contracts available</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-success" />
                <span>Flexible payment schedules</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Award" size={16} className="text-warning" />
                <span>100% satisfaction guarantee</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingCalculator;